import { useState } from "react";
import { useSoftphone } from "softphone";
import { Button, ButtonSize, ButtonVariant } from "shared/components";
import { MakeCallModal } from "./MakeCallModal";
import type { CallSelection } from "./useCallSelection";

interface VoiceComposerProps {
  contactId?: string;
  selection: CallSelection;
}

/**
 * Voice tab in the inbox composer: the From/To pickers live in the composer
 * header (see MessageComposer) and this row below holds "Make a call", pinned
 * right like the manual tab's submit button. It opens the confirmation modal
 * (pre-filled with the header selection) before the softphone dials. While a
 * call is connected the app-wide call card/pill owns the UI.
 *
 * The text-to-speech announcement feature is intentionally hidden from the UI
 * until it's productized; the server-side `voiceApi.announce` endpoint remains
 * in place for when it's re-enabled.
 */
export const VoiceComposer = ({ contactId, selection }: VoiceComposerProps) => {
  const { activeCall, isReady } = useSoftphone();
  const { fromId, toNumber, toOptions } = selection;
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (activeCall)
    return (
      <div className="px-6 py-4 text-sm text-content-400">
        Call in progress — manage it from the call window.
      </div>
    );

  if (toOptions.length === 0)
    return (
      <div className="px-6 py-4 text-sm text-content-400">
        This contact has no phone number on file — add one to call.
      </div>
    );

  const canCall = !!fromId && !!toNumber && isReady;

  return (
    <div className="px-6 py-4 w-full flex items-center">
      <Button
        variant={ButtonVariant.PRIMARY}
        size={ButtonSize.BASE}
        onClick={() => setConfirmOpen(true)}
        disabled={!canCall}
        className="ml-auto shrink-0"
      >
        Make a call
      </Button>
      {contactId && (
        <MakeCallModal
          opened={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          contactId={contactId}
          initialFromId={fromId}
          initialToNumber={toNumber}
        />
      )}
    </div>
  );
};
